export const QUERY_CACHE_KEYS = {
  currentUser: ['currentUser'] as const,
  auth: {
    settings: ['auth', 'settings'] as const,
  },
  profile: {
    participant: ['profile', 'participant'] as const,
    /** Под корнем профиля: одобрение заявки сбрасывает и прогноз. */
    cityChangeImpact: (cityId: number | null) =>
      ['profile', 'participant', 'city-change-impact', cityId] as const,
    admin: ['profile', 'admin'] as const,
    representative: ['profile', 'representative'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (params?: Record<string, string>) =>
      params ? (['users', 'list', params] as const) : (['users', 'list'] as const),
    detail: (id: number) => ['users', id] as const,
    representatives: (countryId: number | null) => ['users', 'representatives', countryId] as const,
  },
  dashboard: {
    summary: ['dashboard', 'summary'] as const,
    current: ['dashboard', 'current'] as const,
    currentStats: ['dashboard', 'currentStats'] as const,
    trend: (period: string) => ['dashboard', 'trend', period] as const,
  },
  participantDashboard: {
    summary: ['participantDashboard', 'summary'] as const,
    activity: (period: string) => ['participantDashboard', 'activity', period] as const,
    global: ['participantDashboard', 'global'] as const,
    globalSearch: (search: string) => ['participantDashboard', 'global', 'search', search] as const,
    myTasks: ['participantDashboard', 'myTasks'] as const,
  },
  olympiads: {
    all: ['olympiads'] as const,
    list: (params?: Record<string, string>) =>
      params ? (['olympiads', 'list', params] as const) : (['olympiads', 'list'] as const),
    detail: (id: number) => ['olympiads', id] as const,
    countries: (id: number) => ['olympiads', id, 'countries'] as const,
    /** Admin per-olympiad participants screen: и корзина пагинации, и корень
     *  кеша списка регистраций. */
    participants: ['olympiads', 'participants'] as const,
    participantsList: (id: number, params?: Record<string, string>) =>
      params
        ? (['olympiads', 'participants', id, params] as const)
        : (['olympiads', 'participants', id] as const),
    participantsFilters: (id: number) => ['olympiads', 'participants', id, 'filters'] as const,
    participantAll: ['olympiads', 'participant'] as const,
    /** Активные олимпиады без пагинации (плашка главной). Под корнем
     *  `participantAll`, поэтому его инвалидация после регистрации цепляет и их. */
    participantActive: ['olympiads', 'participant', 'active'] as const,
    /** Недавно завершённые (первая страница вкладки) — окна отмены и
     *  дисквалификации при входе. Тоже под корнем `participantAll`. */
    participantFinishedRecent: ['olympiads', 'participant', 'finished-recent'] as const,
    participantList: (params?: Record<string, string>) =>
      params
        ? (['olympiads', 'participant', 'list', params] as const)
        : (['olympiads', 'participant', 'list'] as const),
    participantDetail: (id: number) => ['olympiads', 'participant', id] as const,
    participantProctorStatus: (id: number) =>
      ['olympiads', 'participant', id, 'proctor-status'] as const,
    common: ['olympiads', 'common'] as const,
  },
  tasks: {
    all: (params?: Record<string, string>) =>
      params ? (['tasks', params] as const) : (['tasks'] as const),
    detail: (id: number) => ['tasks', id] as const,
    // Participant keys carry their query params: grade and language change WHICH
    // tasks and which translation come back, so a key without them serves one
    // grade's list to another. `source` is 'daily' | 'general'.
    participantList: (source: string, params?: Record<string, string>) =>
      params
        ? (['tasks', 'participant', source, 'list', params] as const)
        : (['tasks', 'participant', source, 'list'] as const),
    participantDetail: (source: string, id: number, params?: Record<string, string>) =>
      params
        ? (['tasks', 'participant', source, id, params] as const)
        : (['tasks', 'participant', source, id] as const),
    /** Everything under one source — used to invalidate after an action. */
    participantSource: (source: string) => ['tasks', 'participant', source] as const,
    participantMission: (type: string, grade?: number) =>
      ['tasks', 'participant', 'mission', type, grade ?? null] as const,
    /** Every grade's mission for one type — the prefix to invalidate after an action. */
    participantMissionType: (type: string) => ['tasks', 'participant', 'mission', type] as const,
  },
  results: {
    list: (params?: Record<string, string>) =>
      params ? (['results', params] as const) : (['results'] as const),
    participants: (params?: Record<string, string>) =>
      params
        ? (['results', 'participants', params] as const)
        : (['results', 'participants'] as const),
    stats: ['results', 'stats'] as const,
    summary: (olympiadId?: string) =>
      olympiadId
        ? (['results', 'summary', olympiadId] as const)
        : (['results', 'summary'] as const),
    countriesRanking: ['results', 'countries', 'ranking'] as const,
    countriesCharts: (params?: Record<string, string>) =>
      params
        ? (['results', 'countries', 'charts', params] as const)
        : (['results', 'countries', 'charts'] as const),
    countriesParticipants: (params?: Record<string, string>) =>
      params
        ? (['results', 'countries', 'participants', params] as const)
        : (['results', 'countries', 'participants'] as const),
    // Participant
    participantRankingSummary: ['results', 'participant', 'ranking', 'summary'] as const,
    participantRankingOlympiad: (params?: Record<string, string>) =>
      params
        ? (['results', 'participant', 'ranking', 'olympiad', params] as const)
        : (['results', 'participant', 'ranking', 'olympiad'] as const),
    participantRankingGlobal: (params?: Record<string, string>) =>
      params
        ? (['results', 'participant', 'ranking', 'global', params] as const)
        : (['results', 'participant', 'ranking', 'global'] as const),
    participantPathProgress: ['results', 'participant', 'path', 'progress'] as const,
    participantPathResults: ['results', 'participant', 'path', 'results'] as const,
    participantPathAchievements: ['results', 'participant', 'path', 'achievements'] as const,
    // Representative
    repStats: ['results', 'rep', 'stats'] as const,
    repCurrentOlympiad: ['results', 'rep', 'currentOlympiad'] as const,
    repParticipants: (params?: Record<string, string>) =>
      params
        ? (['results', 'rep', 'participants', params] as const)
        : (['results', 'rep', 'participants'] as const),
    repDynamics: ['results', 'rep', 'dynamics'] as const,
    repSummary: ['results', 'rep', 'summary'] as const,
    repMemberDetail: (id: number) => ['results', 'rep', 'member', id] as const,
    repMemberProfile: (id: number) => ['results', 'rep', 'member', id, 'profile'] as const,
    repMemberProgress: (id: number) => ['results', 'rep', 'member', id, 'progress'] as const,
    repMemberAchievements: (id: number) =>
      ['results', 'rep', 'member', id, 'achievements'] as const,
    repMemberResults: (id: number) => ['results', 'rep', 'member', id, 'results'] as const,
    repMemberStatuses: (id: number) => ['results', 'rep', 'member', id, 'statuses'] as const,
    repOlympiadSummary: (id: number) => ['results', 'rep', 'olympiad', id, 'summary'] as const,
    repOlympiadRanking: (id: number) => ['results', 'rep', 'olympiad', id, 'ranking'] as const,
  },
  payments: {
    list: (params?: Record<string, string>) =>
      params ? (['payments', params] as const) : (['payments'] as const),
    participantBalance: ['payments', 'participant', 'balance'] as const,
    participantHistory: ['payments', 'participant', 'history'] as const,
  },
  certificates: {
    list: (params?: Record<string, string>) =>
      params ? (['certificates', params] as const) : (['certificates'] as const),
    participantAll: ['certificates', 'participant'] as const,
  },
  common: {
    countries: ['common', 'countries'] as const,
    olympiads: ['common', 'olympiads'] as const,
  },
  locations: {
    countries: ['locations', 'countries'] as const,
    cities: (country?: string) =>
      country ? (['locations', 'cities', country] as const) : (['locations', 'cities'] as const),
  },
  chat: {
    representatives: ['chat', 'representatives'] as const,
    administrators: ['chat', 'administrators'] as const,
    privateMessages: (profileId: number) => ['chat', 'private', profileId] as const,
    support: {
      /** Корень кеша поддержки: инвалидируется целиком после отправки —
       *  сообщение меняет и тред, и превью в списке, и счётчик непрочитанных. */
      all: ['chat', 'support'] as const,
      active: ['chat', 'support', 'active'] as const,
      activeMessages: ['chat', 'support', 'active', 'messages'] as const,
      tickets: (status?: string) =>
        status
          ? (['chat', 'support', 'tickets', status] as const)
          : (['chat', 'support', 'tickets'] as const),
      ticketMessages: (id: number) => ['chat', 'support', 'tickets', id, 'messages'] as const,
    },
    adminSupport: {
      /** Корень: ответ меняет и тред, и превью в списке, и счётчик непрочитанных. */
      all: ['chat', 'admin-support'] as const,
      rooms: (params?: Record<string, string>) =>
        ['chat', 'admin-support', 'rooms', params ?? {}] as const,
      room: (id: number) => ['chat', 'admin-support', 'rooms', id] as const,
      roomMessages: (id: number) => ['chat', 'admin-support', 'rooms', id, 'messages'] as const,
    },
  },
} as const
